import React, { useEffect, useState } from 'react';
import {
    Modal, Button, Table, Space, Input, InputNumber, Form, Select,
    Tooltip, Typography, Popconfirm, message
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { COUNTRY_OPTIONS, getCountryLabel, fenToYuan, yuanToFen, formatUsdFen } from '../../../../utils/regionPricing';

const { Text } = Typography;

// ══════════════════════════════════════════════
//  区域定价模板管理
//  模板 = 一套区域定价配置（海外默认价 + 国家覆盖白名单），仅用于「快捷填充」。
//  修改 / 删除模板采用「快照」语义：不回写已保存套餐，不影响已使用该模板的套餐定价。
// ──────────────────────────────────────────────

const RegionPricingTemplateManageDialog = ({ open, templates = [], onChange, onCancel }) => {
    const [form] = Form.useForm();
    const [editor, setEditor] = useState({ open: false, editId: null });

    // 关闭管理弹窗时一并收起编辑器
    useEffect(() => {
        if (!open) setEditor({ open: false, editId: null });
    }, [open]);

    // 打开编辑器时回显 / 清空
    useEffect(() => {
        if (!editor.open) return;
        const current = editor.editId ? templates.find(t => t.id === editor.editId) : null;
        form.resetFields();
        if (current) {
            form.setFieldsValue({
                name: current.name,
                defaultUsd: fenToYuan(current.defaultUsd),
                countryOverrides: (current.countryOverrides || []).map(c => ({ country: c.country, usdPrice: fenToYuan(c.usdPrice) })),
            });
        }
    }, [editor, templates, form]);

    const handleSubmit = async () => {
        const values = await form.validateFields();
        const name = (values.name || '').trim();
        if (templates.some(t => t.name === name && t.id !== editor.editId)) {
            message.warning('模板名称已存在');
            return;
        }
        const countries = (values.countryOverrides || []).filter(r => r && r.country);
        const defaultUsd = yuanToFen(values.defaultUsd);
        if ((defaultUsd === undefined || defaultUsd === null) && countries.length === 0) {
            message.warning('请至少配置海外默认价或一个国家覆盖');
            return;
        }
        const payload = {
            name,
            defaultUsd: defaultUsd ?? null,
            countryOverrides: countries.map(r => ({ country: r.country, usdPrice: yuanToFen(r.usdPrice) ?? null })),
        };
        if (editor.editId) {
            onChange(templates.map(t => (t.id === editor.editId ? { ...t, ...payload } : t)));
            message.success('模板已更新');
        } else {
            onChange([...templates, { id: `rpt_${Date.now()}`, ...payload }]);
            message.success('模板已创建');
        }
        setEditor({ open: false, editId: null });
    };

    const handleDelete = (row) => {
        onChange(templates.filter(t => t.id !== row.id));
        message.success(`已删除模板「${row.name}」`);
    };

    const columns = [
        { title: '模板名称', dataIndex: 'name', key: 'name', width: 180, render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
        { title: '海外默认价', key: 'defaultUsd', width: 120, render: (_, r) => formatUsdFen(r.defaultUsd) },
        {
            title: '国家覆盖', key: 'country', width: 180,
            render: (_, r) => {
                const countries = (r.countryOverrides || []).filter(c => c && c.country);
                if (!countries.length) return <Text type="secondary">未设置</Text>;
                return (
                    <Tooltip title={countries.map(c => `${getCountryLabel(c.country)} ${formatUsdFen(c.usdPrice)}`).join('、')}>
                        <span style={{ cursor: 'default' }}>{countries.length} 国覆盖</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '操作', key: 'action', width: 160, align: 'center',
            render: (_, row) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setEditor({ open: true, editId: row.id })}>编辑</Button>
                    <Popconfirm
                        title={`删除模板「${row.name}」？`}
                        description="删除后不影响已使用该模板的套餐定价。"
                        okText="确定" cancelText="取消" okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Modal
                title="区域定价模板管理"
                open={open}
                onCancel={onCancel}
                width={720}
                destroyOnClose
                zIndex={1050}
                footer={<Button icon={<CloseOutlined />} onClick={onCancel}>关闭</Button>}
            >
                <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditor({ open: true, editId: null })}>新增模板</Button>
                </div>

                <Table dataSource={templates} columns={columns} rowKey="id" pagination={false} size="small" />

                <div style={{ marginTop: 12, fontSize: 13, color: '#909399' }}>
                    模板用于在套餐里一键填充区域定价；修改或删除模板不会改变已保存套餐的定价。
                </div>

                <Modal
                    title={editor.editId ? '编辑区域定价模板' : '新增区域定价模板'}
                    open={editor.open}
                    onCancel={() => setEditor({ open: false, editId: null })}
                    width={520}
                    maskClosable={false}
                    zIndex={1100}
                    footer={(
                        <Space>
                            <Button icon={<CloseOutlined />} onClick={() => setEditor({ open: false, editId: null })}>取消</Button>
                            <Button type="primary" icon={<CheckOutlined />} onClick={handleSubmit}>保存</Button>
                        </Space>
                    )}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item label="模板名称" name="name" rules={[{ required: true, message: '请输入模板名称' }]}>
                            <Input placeholder="请输入模板名称" maxLength={30} showCount />
                        </Form.Item>

                        <Form.Item label="海外默认价（$）" name="defaultUsd" extra="海外未单独定价的国家按此价售卖">
                            <InputNumber min={0.01} precision={2} controls={false} prefix="$" placeholder="0.00" style={{ width: '100%' }} />
                        </Form.Item>

                        <div style={{ fontSize: 13, color: '#606266', margin: '4px 0 12px' }}>国家覆盖（可选，覆盖海外默认价）</div>
                        <Form.List name="countryOverrides">
                            {(fields, { add, remove }) => (
                                <div>
                                    {fields.length === 0 && (
                                        <div style={{ fontSize: 12, color: '#bfbfbf', marginBottom: 12 }}>未设置国家覆盖，海外统一按默认价售卖</div>
                                    )}
                                    {fields.map(({ key, name, ...restField }) => (
                                        <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'baseline' }}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'country']}
                                                style={{ flex: 1, marginBottom: 0 }}
                                                rules={[
                                                    { required: true, message: '请选择国家' },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (!value) return Promise.resolve();
                                                            const list = getFieldValue('countryOverrides') || [];
                                                            const count = list.filter(r => r && r.country === value).length;
                                                            return count > 1 ? Promise.reject(new Error('国家不可重复')) : Promise.resolve();
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Select placeholder="选择国家" options={COUNTRY_OPTIONS} allowClear showSearch optionFilterProp="label" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'usdPrice']}
                                                style={{ flex: '0 0 180px', marginBottom: 0 }}
                                                rules={[{ required: true, message: '请输入售价' }]}
                                            >
                                                <InputNumber min={0.01} precision={2} controls={false} prefix="$" placeholder="售价" style={{ width: '100%' }} />
                                            </Form.Item>
                                            <Button type="link" danger onClick={() => remove(name)}>删除</Button>
                                        </div>
                                    ))}
                                    <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ country: undefined, usdPrice: undefined })}>
                                        添加国家覆盖
                                    </Button>
                                </div>
                            )}
                        </Form.List>
                    </Form>
                </Modal>
            </Modal>
        </>
    );
};

export default RegionPricingTemplateManageDialog;
